import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  DragIndicator as DragIndicatorIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { NotificationManager } from "react-notifications";
import { ReorderQuoteTransactions } from "../../../vlp/services/QuotesService";

/**
 * Re-order the transactions on a quote, the Extend equivalent of the VLP
 * quotranssort.aspx pop-up. Items can be dragged or nudged with the arrow buttons;
 * saving writes the new order to quotrans.sort_order.
 */
export default function ReorderQuoteTransactionsDialog({
  open,
  onClose,
  quoteNumber,
  quotransData,
  onReorderComplete,
}) {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  // Always start from the stored order rather than whatever column the grid is
  // currently sorted by, so the list reflects what is actually saved.
  useEffect(() => {
    if (!open) return;

    const ordered = [...(quotransData || [])].sort((a, b) => {
      const aSort = a.sortOrder ?? 0;
      const bSort = b.sortOrder ?? 0;
      if (aSort !== bSort) return aSort - bSort;
      return (a.recordid ?? 0) - (b.recordid ?? 0);
    });

    setItems(ordered);
  }, [open, quotransData]);

  const move = (index, offset) => {
    const targetIndex = index + offset;
    if (targetIndex < 0 || targetIndex >= items.length) return;

    const reordered = [...items];
    [reordered[index], reordered[targetIndex]] = [
      reordered[targetIndex],
      reordered[index],
    ];
    setItems(reordered);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const reordered = [...items];
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setItems(reordered);
  };

  const handleSave = async () => {
    if (items.length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      await ReorderQuoteTransactions(
        quoteNumber,
        items.map((item) => item.recordid)
      );
      NotificationManager.success("Item order saved", "Success");
      if (onReorderComplete) onReorderComplete(items);
      onClose();
    } catch (error) {
      NotificationManager.error("Error saving the item order. " + error, "Error");
    } finally {
      setSaving(false);
    }
  };

  const itemLabel = (item) => {
    const description = (item.description || "").trim();
    if (description) return description;
    const testCode = (item.testCode || "").trim();
    return testCode || `Record ${item.recordid}`;
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Re-order Items{quoteNumber ? ` for Quote ${quoteNumber}` : ""}
      </DialogTitle>

      <DialogContent dividers>
        {items.length === 0 ? (
          <Typography variant="body2" sx={{ py: 4 }} align="center">
            There are no transactions on this quote to re-order.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Drag an item, or use the arrows, to set the order items are listed in.
              Click Save Changes to apply.
            </Typography>

            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="quotrans" direction="vertical">
                {(provided) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {items.map((item, index) => (
                      <Draggable
                        key={item.recordid}
                        draggableId={item.recordid.toString()}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              px: 1,
                              py: 0.5,
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                              backgroundColor: snapshot.isDragging
                                ? "action.selected"
                                : "background.paper",
                            }}
                            style={provided.draggableProps.style}
                          >
                            <DragIndicatorIcon fontSize="small" color="action" />

                            <Typography
                              variant="body2"
                              sx={{ width: 28, color: "text.secondary" }}
                            >
                              {index + 1}.
                            </Typography>

                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography variant="body2" noWrap title={itemLabel(item)}>
                                {itemLabel(item)}
                              </Typography>
                              {item.testCode && item.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {item.testCode.trim()}
                                </Typography>
                              )}
                            </Box>

                            <IconButton
                              size="small"
                              aria-label="move up"
                              disabled={index === 0}
                              onClick={() => move(index, -1)}
                            >
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="move down"
                              disabled={index === items.length - 1}
                              onClick={() => move(index, 1)}
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || items.length === 0}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
